/**
 * The three catalogs the dashboard module publishes: sections (full, for the
 * Select-location modal), sections (flat, for the filter) and categories.
 *
 * Contract source: `dashboard_module_frontend_contract.md` §8–§10.
 */

import { validateApiResponse, normalizePagedMeta } from '../shared/utils/apiResponse.js';
import { apiDashboards, DASHBOARD_API_PREFIX } from './apiClient.js';

const DEFAULT_PAGE_SIZE = 50;

/**
 * `section/list` → the tree the Select-location modal walks.
 *
 * The contract groups `sectionType → modules`; the modal navigates children,
 * so the group becomes a non-selectable node and each module a selectable
 * leaf. `allowsUserDashboards` is what makes a leaf selectable — a section
 * that cannot host a dashboard must not be clickable.
 *
 * Everything the builder needs after the selection (context schema, grid,
 * breakpoints) rides along on the leaf, which is why there is no second GET.
 */
export const mapApiSectionGroupToUi = (raw = {}) => ({
  id: raw.sectionType ?? '',
  name: raw.name ?? raw.sectionType ?? '',
  icon: raw.icon ?? null,
  supportsDashboard: false,
  children: (raw.modules ?? []).map((module) => ({
    id: module.sectionKey ?? '',
    sectionUuid: module.sectionUuid ?? '',
    sectionKey: module.sectionKey ?? '',
    module: module.module ?? '',
    name: module.name ?? '',
    description: module.description ?? '',
    icon: module.icon ?? null,
    supportsDashboard: module.allowsUserDashboards ?? false,
    allowsTemplates: module.allowsTemplates ?? false,
    isSingleton: module.isSingleton ?? false,
    modelCode: module.modelCode ?? null,
    displayOrder: module.displayOrder ?? 0,
    contextSchema: module.contextSchema ?? {},
    contextUiSchema: module.contextUiSchema ?? {},
    defaultContextValues: module.defaultContextValues ?? {},
    gridColumns: module.gridColumns ?? 12,
    allowedBreakpoints: module.allowedBreakpoints ?? ['DESKTOP'],
    publishedVersionNumber: module.publishedVersionNumber ?? null,
    children: [],
  })),
});

/** `section/list_filter` → one flat option for the Section filter. */
export const mapApiSectionFilterToUi = (raw = {}) => ({
  id: raw.sectionKey ?? '',
  sectionUuid: raw.sectionUuid ?? '',
  sectionKey: raw.sectionKey ?? '',
  sectionType: raw.sectionType ?? '',
  module: raw.module ?? '',
  name: raw.name ?? raw.sectionKey ?? '',
  icon: raw.icon ?? null,
});

/** `category/list_filter` → one option for the Categories filter. */
export const mapApiDashboardCategoryToUi = (raw = {}) => ({
  id: raw.categoryKey ?? '',
  categoryKey: raw.categoryKey ?? '',
  name: raw.name ?? raw.categoryKey ?? '',
  icon: raw.icon ?? null,
  displayOrder: raw.displayOrder ?? 0,
});

/** Sections with everything the builder needs after a selection. */
export async function listSections({ search, page, pageSize } = {}) {
  const response = await apiDashboards.post(`${DASHBOARD_API_PREFIX}/section/list`, {
    ...(search ? { searchKeyword: search } : {}),
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    orderBy: 'display_order',
    orderDirection: 'ASC',
  });
  const { body, meta } = validateApiResponse(response, 'Could not load sections.');
  return {
    sections: (body ?? []).map(mapApiSectionGroupToUi),
    meta: normalizePagedMeta(meta),
  };
}

/** Flat sections for the Section filter on /dashboards. */
export async function listSectionFilters({ search, page, pageSize } = {}) {
  const response = await apiDashboards.post(`${DASHBOARD_API_PREFIX}/section/list_filter`, {
    ...(search ? { searchKeyword: search } : {}),
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    orderBy: 'display_order',
    orderDirection: 'ASC',
  });
  const { body, meta } = validateApiResponse(response, 'Could not load sections.');
  return {
    sections: (body ?? []).map(mapApiSectionFilterToUi),
    meta: normalizePagedMeta(meta),
  };
}

/**
 * Dashboard categories. Its own catalog (`dsh_dashboard_categories`) —
 * distinct from the widget categories, which is why this lives here and not
 * in `widgetCatalogs.js`.
 */
export async function listDashboardCategories({ search, page, pageSize } = {}) {
  const response = await apiDashboards.post(`${DASHBOARD_API_PREFIX}/category/list_filter`, {
    ...(search ? { searchKeyword: search } : {}),
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    orderBy: 'display_order',
    orderDirection: 'ASC',
  });
  const { body, meta } = validateApiResponse(response, 'Could not load dashboard categories.');
  return {
    categories: (body ?? []).map(mapApiDashboardCategoryToUi),
    meta: normalizePagedMeta(meta),
  };
}
